/**
 * Presentation for GET /api/v1/equipment-maintenance/summary.
 *
 * The due wording here is a deliberate port of `dueText()` in the API's
 * equipment-maintenance.routes.ts, which renders the maintenance history PDF
 * from the SAME rows this screen reads. The screen and the printed document must
 * not disagree about a unit, so the phrasing is copied rather than reinvented —
 * in particular:
 *
 *  - `needs_baseline` never says "N jobs past". The count is real but it was
 *    measured from the beginning of records rather than from a service, so
 *    phrasing it as lateness asserts the one thing the status exists to deny.
 *  - `unknown` says WHY. The server explains every signal it declined to
 *    evaluate; dropping that leaves a blank cell indistinguishable from "fine".
 *  - `no_schedule` is a sentence, not an empty cell.
 *
 * Nothing here recomputes due state. Status, remaining and the projection all
 * arrive on the row; this module only chooses words for them.
 */
import { SCHEDULE_STATUS_BADGE, formatScheduleDate } from './maintenanceTriggers';
import type {
  MaintenanceDueSignal,
  UnitDueStatus,
  UnitMaintenanceSummaryRow,
} from '../types/equipment';
import { fmtQty } from './display-labels';

/* ── Status chips ──────────────────────────────────────────────────── */

/** Neutral chip — also the landing spot for a status this build has never seen. */
const NEUTRAL_CHIP = 'bg-fieldiq-dark text-fieldiq-muted border-fieldiq-border';

/**
 * Reuses the schedule chips verbatim and adds the one state only the roll-up can
 * produce. `no_schedule` is the absence of every signal, so it reads quieter than
 * `unknown` (which is a signal the server could not resolve).
 */
export const UNIT_STATUS_BADGE: Record<UnitDueStatus, { label: string; cls: string }> = {
  ...SCHEDULE_STATUS_BADGE,
  no_schedule: { label: 'No Schedule', cls: 'bg-transparent text-fieldiq-muted border-fieldiq-border' },
};

/** Severity, most urgent first — the API's SORT_RANK, used for tile order. */
export const UNIT_STATUS_ORDER: UnitDueStatus[] = [
  'overdue',
  'due_soon',
  'needs_baseline',
  'unknown',
  'ok',
  'no_schedule',
];

/** "needs_recalibration" → "Needs Recalibration". */
function humanizeStatus(status: string): string {
  return status
    .split('_')
    .filter(Boolean)
    .map((word) => (word.length <= 2 ? word.toUpperCase() : word[0].toUpperCase() + word.slice(1)))
    .join(' ');
}

/**
 * Chip for any status string, including one added server-side after this build
 * shipped. An unrecognised status renders as a neutral, humanised chip — never
 * as a raw slug, and never as a colour that would assert urgency we cannot know.
 */
export function unitStatusChip(status: string): { label: string; cls: string } {
  return UNIT_STATUS_BADGE[status as UnitDueStatus] ?? { label: humanizeStatus(status), cls: NEUTRAL_CHIP };
}

/* ── Count tiles ───────────────────────────────────────────────────── */

export interface StatusTile {
  status: string;
  label: string;
  count: number;
  cls: string;
}

/** `counts` carries a fleet total alongside the buckets; it is not a status. */
const TOTAL_KEY = 'total';

/**
 * Tiles built by reading the KEYS off `counts`, not from a hard-coded list.
 *
 * Known statuses come first in severity order; anything the server has added
 * since this build follows, alphabetically. A new bucket therefore appears on
 * the screen the day the API starts returning it rather than silently vanishing.
 */
export function statusTiles(counts: Record<string, number> | undefined | null): StatusTile[] {
  if (!counts) return [];

  const keys = Object.keys(counts).filter((k) => k !== TOTAL_KEY);
  const rank = (k: string) => {
    const i = UNIT_STATUS_ORDER.indexOf(k as UnitDueStatus);
    return i === -1 ? UNIT_STATUS_ORDER.length : i;
  };

  return keys
    .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
    .map((status) => {
      const chip = unitStatusChip(status);
      return { status, label: chip.label, cls: chip.cls, count: Number(counts[status] ?? 0) };
    });
}

/** Fleet size, when the response carried one. */
export function totalUnits(counts: Record<string, number> | undefined | null): number | null {
  const total = counts?.[TOTAL_KEY];
  return typeof total === 'number' ? total : null;
}

/* ── Due column ────────────────────────────────────────────────────── */

/** @see fmtQty — thousands separators, no trailing zeros, absent is '—' not '0'. */
const fmtNum = fmtQty;

const plural = (n: number, word: string) => `${word}${Math.abs(n) === 1 ? '' : 's'}`;

/** The signal the row's headline figures came from. */
export function topSignal(row: UnitMaintenanceSummaryRow): MaintenanceDueSignal | null {
  const signals = row.signals ?? [];
  if (signals.length === 0) return null;
  return (
    signals.find((s) => s.source === row.due_source && s.schedule_id === row.due_schedule_id) ??
    signals.find((s) => s.status === row.status) ??
    signals[0]
  );
}

/**
 * Why the row reads the way it does — the server's own account. Surfaced as the
 * chip's tooltip, and as the due text itself for `unknown`.
 */
export function dueExplanation(row: UnitMaintenanceSummaryRow): string | undefined {
  const signal = topSignal(row);
  return signal?.error || signal?.basis || undefined;
}

/** What produced the figure: a named schedule, or the hand-typed legacy date. */
export function dueRuleLabel(row: UnitMaintenanceSummaryRow): string | null {
  if (row.due_source === 'legacy_record') return 'Manually entered date';
  return row.due_schedule_name ?? null;
}

/**
 * One unit's due state as a phrase. Ported from the PDF's `dueText()`.
 *
 * Every status returns a sentence. A bare number is never returned, and a past
 * date is rendered as a past date — an overdue unit showing an empty cell was
 * the original bug this report was rewritten to fix.
 */
export function unitDueText(row: UnitMaintenanceSummaryRow): string {
  if (row.status === 'no_schedule') return 'No schedule set';

  // The server explains what it declined to evaluate — that reason IS the answer.
  if (row.status === 'unknown') {
    const reason = dueExplanation(row);
    if (reason) return reason;
    if (row.next_due_at === null && row.next_due_value === null) return 'Cannot be evaluated';
  }

  // Never phrase this as time past due. The interval is met, but it was measured
  // from the beginning of records rather than from a service, so "N jobs past"
  // would assert exactly the lateness the status exists to stop claiming.
  if (row.status === 'needs_baseline') {
    const seen =
      row.next_due_value != null && row.due_remaining != null
        ? row.next_due_value - row.due_remaining
        : null;
    const unit = row.due_unit ?? 'units';
    return seen != null
      ? `No service ever recorded — ${fmtNum(seen)} ${unit} logged against a ` +
          `${fmtNum(row.next_due_value as number)}-${unit.replace(/s$/, '')} interval`
      : 'No service ever recorded';
  }

  const rem = row.due_remaining;
  const overdueBy = rem != null && rem <= 0 ? Math.abs(rem) : null;

  if (row.next_due_at) {
    const d = formatScheduleDate(row.next_due_at);
    if (overdueBy !== null) return `${d} — ${fmtNum(overdueBy)} ${plural(overdueBy, 'day')} overdue`;
    return rem != null ? `${d} (in ${fmtNum(rem)} ${plural(rem, 'day')})` : d;
  }

  if (row.next_due_value != null) {
    const unit = row.due_unit ?? '';
    const at = `at ${fmtNum(row.next_due_value)} ${unit}`.trim();
    if (overdueBy !== null) return `${at} — ${fmtNum(overdueBy)} ${unit} past`.trim();
    return rem != null ? `${at} (${fmtNum(rem)} ${unit} to go)`.trim() : at;
  }

  return 'Not determined';
}

/** The unit's display label, with the server's fallbacks already applied. */
export function unitLabel(row: UnitMaintenanceSummaryRow): string {
  return row.unit_number || row.equipment_name || 'Unnamed unit';
}
